import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  IconButton,
  Typography,
  Stack,
  Alert,
  useTheme,
  useMediaQuery,
  Drawer,
  Fab,
  AppBar,
  Toolbar,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MenuIcon from '@mui/icons-material/Menu';
import ContactList from './components/ContactList';
import ContactDetail from './components/ContactDetail';
import ContactForm from './components/ContactForm';
import ContactImport from './components/ContactImport';
import {
  getContacts, createContact, updateContact, deleteContact,
} from '../../api/contact.api';

function ContactsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedContact, setSelectedContact] = useState(null);
  const [tab, setTab] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Fetch contacts on component mount
  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const data = await getContacts();
      console.log('Fetched contacts:', data);
      let contactsArray = [];
      if (Array.isArray(data)) {
        contactsArray = data;
      } else if (data && Array.isArray(data.data)) {
        contactsArray = data.data;
      }
      // Log the array being set
      console.log('Setting contacts:', contactsArray);
      setContacts(contactsArray);
      setError(null);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to load contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = () => setAddDialogOpen(true);
  const handleCloseAddDialog = () => setAddDialogOpen(false);

  const handleSaveContact = async (contactData) => {
    try {
      if (contactData._id) {
        // Update existing contact
        await updateContact(contactData._id, contactData);
      } else {
        // Create new contact
        await createContact(contactData);
      }
      setAddDialogOpen(false);
      setTab(0);
      fetchContacts(); // Refresh the list
    } catch (err) {
      console.error('Error saving contact:', err);
      setError('Failed to save contact. Please try again.');
    }
  };

  const handleContactDelete = async (contact) => {
    try {
      await deleteContact(contact._id);
      fetchContacts(); // Refresh the list
      if (selectedContact?._id === contact._id) {
        setSelectedContact(null);
      }
    } catch (err) {
      console.error('Error deleting contact:', err);
      setError('Failed to delete contact. Please try again.');
    }
  };

  const handleBulkAction = async (action, contacts) => {
    try {
      // Handle bulk actions here
      console.log(`Bulk action: ${action}`, contacts);
      fetchContacts(); // Refresh the list
    } catch (err) {
      console.error('Error performing bulk action:', err);
      setError('Failed to perform bulk action. Please try again.');
    }
  };

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    if (isMobile) {
      setMobileDrawerOpen(false); // Close drawer on mobile when contact is selected
    }
  };

  // Mobile layout
  if (isMobile) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Mobile App Bar */}
        <AppBar position="static" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1 }}>
              Contacts
            </Typography>
            <Button
              color="inherit"
              startIcon={<PersonAddIcon />}
              onClick={handleAddContact}
            >
              Add
            </Button>
          </Toolbar>
        </AppBar>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {selectedContact ? (
            // Contact Detail View
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Paper elevation={0} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab label="Details" />
                  <Tab label="Edit" />
                </Tabs>
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  {tab === 0 && (
                    <ContactDetail
                      contact={selectedContact}
                      onEdit={(contact) => {
                        setSelectedContact(contact);
                        setTab(1);
                      }}
                    />
                  )}
                  {tab === 1 && (
                    <ContactForm
                      contact={selectedContact}
                      onSave={handleSaveContact}
                      mode="edit"
                    />
                  )}
                </Box>
              </Paper>
            </Box>
          ) : (
            // Empty State
            <Box sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              p: 3,
            }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a Contact
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Choose a contact from the list to view details
              </Typography>
            </Box>
          )}
        </Box>

        {/* Mobile Drawer for Contact List */}
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: isSmallMobile ? '100%' : 320,
              boxSizing: 'border-box',
            },
          }}
        >
          <Box sx={{
            p: 2, height: '100%', display: 'flex', flexDirection: 'column',
          }}
          >
            <Box sx={{ mb: 2 }}>
              <ContactImport onImportSuccess={fetchContacts} />
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <ContactList
                contacts={contacts}
                loading={loading}
                onContactSelect={handleContactSelect}
                onContactEdit={(contact) => {
                  setSelectedContact(contact);
                  setTab(1);
                  setMobileDrawerOpen(false);
                }}
                onContactDelete={handleContactDelete}
                onBulkAction={handleBulkAction}
                selectedContacts={selectedContacts}
                onSelectionChange={setSelectedContacts}
                onAddContact={handleAddContact}
              />
            </Box>
          </Box>
        </Drawer>

        {/* Add Contact Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              borderRadius: isSmallMobile ? 0 : 3,
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 2,
            px: 3,
          }}
          >
            <PersonAddIcon sx={{ fontSize: 32 }} />
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
              Add New Contact
            </Typography>
            <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <ContactForm onSave={handleSaveContact} onCancel={handleCloseAddDialog} mode="add" />
          </Box>
        </Dialog>
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 }, height: '100vh', display: 'flex', flexDirection: 'column',
    }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              minHeight: 0,
            }}
          >
            <Box sx={{ mb: 2 }}>
              <ContactImport onImportSuccess={fetchContacts} />
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <ContactList
                contacts={contacts}
                loading={loading}
                onContactSelect={handleContactSelect}
                onContactEdit={(contact) => {
                  setSelectedContact(contact);
                  setTab(1);
                }}
                onContactDelete={handleContactDelete}
                onBulkAction={handleBulkAction}
                selectedContacts={selectedContacts}
                onSelectionChange={setSelectedContacts}
                onAddContact={handleAddContact}
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper
            elevation={2}
            sx={{
              height: '100%',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Details" />
              <Tab label="Edit" />
            </Tabs>
            <Box sx={{ mt: 2, flex: 1, overflow: 'auto' }}>
              {tab === 0 && (
                <ContactDetail
                  contact={selectedContact}
                  onEdit={(contact) => {
                    setSelectedContact(contact);
                    setTab(1);
                  }}
                />
              )}
              {tab === 1 && (
                <ContactForm
                  contact={selectedContact}
                  onSave={handleSaveContact}
                  mode="edit"
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 0 } }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <PersonAddIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Add New Contact
          </Typography>
          <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <ContactForm onSave={handleSaveContact} onCancel={handleCloseAddDialog} mode="add" />
        </Box>
      </Dialog>
    </Box>
  );
}

export default ContactsPage;
